import type {
  DataTableDimensionsType,
  DataTableKpiDisplayNameType,
  DataTableViewType,
  ProductTaxonomyType
} from '@carrefour-gcs/shared';
import { defineStore } from 'pinia';

interface DatatableTaxonomyStoreStateType {
  selectedDimensions: {
    [key in DataTableViewType]?: DataTableDimensionsType[];
  };
  selectedKpis: {
    [key in DataTableViewType]?: DataTableKpiDisplayNameType[];
  };
  kpisColumnsOrder: {
    [key in DataTableViewType]?: DataTableKpiDisplayNameType[];
  };
  dimensionsOrder: {
    [key in DataTableViewType]?: DataTableDimensionsType[];
  };
}

interface DatatableStoreStateType {
  taxonomy: {
    [_key in ProductTaxonomyType]?: DatatableTaxonomyStoreStateType;
  };
}

export const useDataTableStore = defineStore('dataTable', {
  state: (): DatatableStoreStateType => ({
    taxonomy: {}
  }),
  actions: {
    getSelectedDimensions(
      taxonomy: ProductTaxonomyType,
      dataTableView: DataTableViewType
    ): DataTableDimensionsType[] | null {
      return this.taxonomy[taxonomy]?.selectedDimensions?.[dataTableView] ?? null;
    },
    getSelectedKpis(
      taxonomy: ProductTaxonomyType,
      dataTableView: DataTableViewType
    ): DataTableKpiDisplayNameType[] | null {
      return this.taxonomy[taxonomy]?.selectedKpis?.[dataTableView] ?? null;
    },
    getKpisColumnsOrder(
      taxonomy: ProductTaxonomyType,
      dataTableView: DataTableViewType
    ): DataTableKpiDisplayNameType[] | null {
      return this.taxonomy[taxonomy]?.kpisColumnsOrder?.[dataTableView] ?? null;
    },
    getDimensionsOrder(
      taxonomy: ProductTaxonomyType,
      dataTableView: DataTableViewType
    ): DataTableDimensionsType[] | null {
      return this.taxonomy[taxonomy]?.dimensionsOrder?.[dataTableView] ?? null;
    },
    updateSelectedDimensions(
      taxonomy: ProductTaxonomyType,
      dataTableView: DataTableViewType,
      dimensions: DataTableDimensionsType[]
    ): void {
      if (!this.taxonomy[taxonomy]) {
        this.taxonomy[taxonomy] = initDataTableTaxonomy();
      }

      this.taxonomy[taxonomy]!.selectedDimensions[dataTableView] = [...dimensions];
    },
    updateSelectedKpis(
      taxonomy: ProductTaxonomyType,
      dataTableView: DataTableViewType,
      kpis: DataTableKpiDisplayNameType[]
    ): void {
      if (!this.taxonomy[taxonomy]) {
        this.taxonomy[taxonomy] = initDataTableTaxonomy();
      }

      this.taxonomy[taxonomy]!.selectedKpis[dataTableView] = [...kpis];
    },
    updateKpisColumnsOrder(
      taxonomy: ProductTaxonomyType,
      dataTableView: DataTableViewType,
      kpis: DataTableKpiDisplayNameType[]
    ): void {
      if (!this.taxonomy[taxonomy]) {
        this.taxonomy[taxonomy] = initDataTableTaxonomy();
      }

      this.taxonomy[taxonomy]!.kpisColumnsOrder[dataTableView] = [...kpis];
    },
    updateDimensionsOrder(
      taxonomy: ProductTaxonomyType,
      dataTableView: DataTableViewType,
      dimensions: DataTableDimensionsType[]
    ): void {
      if (!this.taxonomy[taxonomy]) {
        this.taxonomy[taxonomy] = initDataTableTaxonomy();
      }

      this.taxonomy[taxonomy]!.dimensionsOrder[dataTableView] = [...dimensions];
    },
    resetSelectedDimensions(taxonomy: ProductTaxonomyType, dataTableView: DataTableViewType): void {
      delete this.taxonomy[taxonomy]?.selectedDimensions[dataTableView];
    },
    resetSelectedKpis(taxonomy: ProductTaxonomyType, dataTableView: DataTableViewType): void {
      delete this.taxonomy[taxonomy]?.selectedKpis[dataTableView];
    },
    resetKpisColumnsOrder(taxonomy: ProductTaxonomyType, dataTableView: DataTableViewType): void {
      delete this.taxonomy[taxonomy]?.kpisColumnsOrder[dataTableView];
    },
    resetDimensionsOrder(taxonomy: ProductTaxonomyType, dataTableView: DataTableViewType): void {
      delete this.taxonomy[taxonomy]?.dimensionsOrder[dataTableView];
    }
  }
});

const initDataTableTaxonomy = (): DatatableTaxonomyStoreStateType => ({
  selectedDimensions: {},
  selectedKpis: {},
  kpisColumnsOrder: {},
  dimensionsOrder: {}
});
